import type { SheetViewProps } from "./Sheet";

export interface SheetDismissalPolicy {
  /** Master gate. `false` makes the surface undismissable by any route. */
  dismissible: boolean;
  /** Escape / hardware back. */
  keyboard: boolean;
  /** Scrim tap. */
  clickOutside: boolean;
  /** Swipe-to-dismiss. Gesture availability, not policy — Silk owns the gesture. */
  swipe: boolean;
}

/**
 * Resolves the house policy for a surface. `alertdialog` hardens the two
 * accidental routes (scrim tap, swipe) and keeps the two deliberate ones
 * (Escape, the close control), per WAI-ARIA APG.
 */
export function resolveSheetDismissal(
  requested: Omit<SheetDismissalPolicy, "clickOutside" | "swipe">,
  role: "dialog" | "alertdialog",
): SheetDismissalPolicy {
  const alert = role === "alertdialog";
  return {
    clickOutside: !alert,
    dismissible: requested.dismissible,
    keyboard: requested.keyboard,
    swipe: !alert,
  };
}

type DismissalProps = Pick<SheetViewProps, "onClickOutside" | "onEscapeKeyDown" | "swipeDismissal">;

/**
 * Silk View props implementing a policy, always in the *function* form.
 *
 * Silk runs function handlers for every role, but the `dismiss` they resolve
 * still feeds its internal dismisser — which `sheetRole="alertdialog"` hard-wires
 * to a no-op (documented: alertdialog computes `onEscapeKeyDown` to
 * `{ dismiss: false }`). Leaving `dismiss` true therefore means Escape silently
 * does nothing on an alertdialog, while `role="dialog"` gets a *second* close
 * path racing the house one. Neutralising `dismiss` and closing through the
 * house state machine gives exactly one path, identical for both roles.
 *
 * `swipeDismissal` stays declarative: it gates a gesture Silk owns end to end,
 * with no house state to keep in step.
 */
export function sheetDismissalProps(policy: SheetDismissalPolicy, close: () => void): DismissalProps {
  return {
    onClickOutside: (event) => {
      event.changeDefault({ dismiss: false, stopOverlayPropagation: true });
      if (policy.dismissible && policy.clickOutside) close();
    },
    onEscapeKeyDown: (event) => {
      event.changeDefault({ dismiss: false, nativePreventDefault: true, stopOverlayPropagation: true });
      if (policy.dismissible && policy.keyboard) close();
    },
    swipeDismissal: policy.dismissible && policy.swipe,
  };
}
